import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_BLACK_KEY = '/logo-black.png';
const LOGO_WHITE_KEY = '/logo-white.png';

export default function ForceChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { theme } = useTheme();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const logo = theme === 'dark' ? LOGO_WHITE_KEY : LOGO_BLACK_KEY;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password-required', {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      updateUser({ must_change_password: false });
      toast.success(t('auth.passwordChangedSuccess'));
      const home = ['admin', 'supervisor'].includes(user?.role) ? '/dashboard' : '/work-requests';
      navigate(home, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || t('auth.passwordChangeFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 transition-colors" data-testid="force-change-password-page">
      <Card className="w-full max-w-md shadow-xl border bg-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <img key={theme} src={logo} alt="Logo" className="w-20 h-20 object-contain mx-auto" />
          </div>
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-3">
            <Lock className="w-6 h-6 text-amber-500" />
          </div>
          <CardTitle className="font-barlow text-xl font-bold text-foreground tracking-tight">
            {t('auth.changePasswordRequired')}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {t('auth.changePasswordRequiredDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-foreground">{t('auth.newPassword')}</Label>
              <Input
                id="new-password"
                type="password"
                placeholder={t('auth.newPasswordPlaceholder')}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                required
                className="bg-input text-foreground border-border"
                data-testid="force-new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-foreground">{t('auth.confirmPassword')}</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder={t('auth.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                required
                className="bg-input text-foreground border-border"
                data-testid="force-confirm-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-red-500" data-testid="force-change-pw-error">{error}</p>
            )}
            <p className="text-xs text-muted-foreground">{t('auth.passwordMinLengthHint')}</p>
            <Button
              type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-brand-foreground"
              disabled={loading}
              data-testid="force-change-pw-submit"
            >
              {loading ? t('common.loading') : t('auth.changePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
